import { Pause, Play } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { drawHero as draw } from '../lib/hero-draw.js';
import { buildHeroScene } from '../lib/hero-scene.js';
import type { TenantGebiet } from '../lib/tenant.js';
import { cn } from '../lib/utils.js';

/**
 * Bewegter Titelbereich (Verwaltung › Erscheinungsbild › Animation): ruhig wandernde Hoehenlinien,
 * darauf das gepflegte Gebiet als Modell, in dem die Orte aus dem Plan wachsen, und je Gemeinde ein
 * Messpunkt mit Namen; zwischen den Messpunkten laufen Daten. Farben aus der Hausfarbe. Ohne Bewegung
 * bei „Bewegung reduzieren“, sonst mit Knopf zum Anhalten (WCAG 2.2.2); ausserhalb des Bildschirms
 * steht sie still.
 */
export function HeroAnimation({
  gebiete,
  hue,
  chroma,
  pauseClassName,
  tile = false,
  playing: outer,
}: {
  gebiete: TenantGebiet[];
  hue: number;
  chroma: number;
  pauseClassName?: string;
  /** Kleine Kachel (Gemeinde): Gebiet rechts, ohne Namen. */
  tile?: boolean;
  /** Von aussen gesteuert (ein gemeinsamer Knopf fuer mehrere Kacheln); dann ohne eigenen Knopf. */
  playing?: boolean;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Nach den Gemeindeschluesseln, nicht nach dem Array: sonst finge die Animation bei jedem Rendern neu an.
  const key = gebiete.map((g) => g.ags).join(',');
  const scene = useMemo(() => buildHeroScene(gebiete), [key]);
  const timeRef = useRef<number | null>(null);
  const reduce = useMemo(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true, []);
  const [own, setPlaying] = useState(!reduce);
  const playing = !reduce && (outer ?? own);
  const playingRef = useRef(playing);
  playingRef.current = playing;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    let raf = 0;
    let visible = true;
    let last = 0;
    // Laufzeit bleibt bei Groessen- oder Farbwechsel erhalten.
    let t = reduce ? 14 : (timeRef.current ?? 0);
    let prev: number | null = null;
    const size = { w: 1, h: 1 };

    const layout = () => {
      const r = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      size.w = Math.max(1, r.width);
      size.h = Math.max(1, r.height);
      canvas.width = Math.round(size.w * dpr);
      canvas.height = Math.round(size.h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const frame = (now: number) => {
      raf = 0;
      if (prev !== null && playingRef.current) t += Math.min(0.1, (now - prev) / 1000);
      timeRef.current = t;
      prev = now;
      if (now - last >= 33) {
        last = now;
        draw(ctx, size.w, size.h, t, scene, hue, chroma, tile);
      }
      if (playingRef.current && visible) raf = requestAnimationFrame(frame);
    };
    const kick = () => {
      if (!raf) {
        prev = null;
        raf = requestAnimationFrame(frame);
      }
    };

    layout();
    draw(ctx, size.w, size.h, t, scene, hue, chroma, tile);
    if (playingRef.current) kick();

    const ro = new ResizeObserver(() => {
      layout();
      draw(ctx, size.w, size.h, t, scene, hue, chroma, tile);
    });
    ro.observe(canvas);
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible && playingRef.current) kick();
    });
    io.observe(canvas);
    (canvas as HTMLCanvasElement & { __kick?: () => void }).__kick = kick;
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
    };
  }, [scene, hue, chroma, reduce, tile]);

  useEffect(() => {
    if (playing) (canvasRef.current as (HTMLCanvasElement & { __kick?: () => void }) | null)?.__kick?.();
  }, [playing]);

  return (
    <>
      <canvas ref={canvasRef} aria-hidden className="absolute inset-0 -z-10 h-full w-full" data-testid="hero-animation" />
      {reduce || outer !== undefined ? null : (
        <button
          type="button"
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? 'Animation anhalten' : 'Animation abspielen'}
          className={cn(
            'absolute z-10 grid h-10 w-10 place-items-center rounded-full border border-white/35 bg-black/40 text-white backdrop-blur-sm hover:bg-black/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
            pauseClassName,
          )}
        >
          {playing ? <Pause className="h-4 w-4" aria-hidden /> : <Play className="h-4 w-4" aria-hidden />}
        </button>
      )}
    </>
  );
}
